import { createContext, useContext, useState, useEffect, useCallback } from "react";
import API from "../services/api";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);

  const logout = useCallback(() => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("authUser");
    setToken(null);
    setUser(null);
  }, []);

  const verifySession = useCallback(async () => {
    const currentToken = localStorage.getItem("authToken");
    if (!currentToken || currentToken === "undefined" || currentToken === "null") {
      return;
    }

    try {
      const res = await API.get("/reader/profile");
      const profile = res.data?.data?.user || res.data?.data || res.data;
      const status = (profile?.status || "").toLowerCase().trim();

      if (status === "inactive" || status === "deactivated") {
        logout();
        if (typeof window !== "undefined" && !window.location.pathname.includes("/login")) {
          window.location.href = "/login";
        }
        return;
      }

      if (profile) {
        setUser(profile);
        localStorage.setItem("authUser", JSON.stringify(profile));
      }
    } catch (err) {
      const errStatus = err?.response?.status;
      const errCode = err?.response?.data?.code;
      if (errStatus === 401 || errStatus === 403 || errCode === "ACCOUNT_DEACTIVATED") {
        logout();
        if (typeof window !== "undefined" && !window.location.pathname.includes("/login")) {
          window.location.href = "/login";
        }
      }
    }
  }, [logout]);

  // Restore user/token from localStorage on mount and verify with backend
  useEffect(() => {
    const storedToken = localStorage.getItem("authToken");
    const storedUser = localStorage.getItem("authUser");

    if (storedToken && storedToken !== "undefined" && storedToken !== "null") {
      setToken(storedToken);
      if (storedUser && storedUser !== "undefined" && storedUser !== "null") {
        try {
          const parsed = JSON.parse(storedUser);
          if (parsed?.status === "inactive" || parsed?.status === "deactivated") {
            logout();
            return;
          }
          setUser(parsed);
        } catch {
          // ignore parsing error
        }
      }
      void verifySession();
    }
  }, [logout, verifySession]);

  // Handle unauthorized events and periodic heartbeat
  useEffect(() => {
    const handleUnauthorized = () => {
      logout();
    };

    const handleFocus = () => {
      if (localStorage.getItem("authToken")) {
        void verifySession();
      }
    };

    window.addEventListener("haven_auth_unauthorized", handleUnauthorized);
    window.addEventListener("focus", handleFocus);

    const interval = setInterval(() => {
      if (localStorage.getItem("authToken")) {
        void verifySession();
      }
    }, 10000);

    return () => {
      window.removeEventListener("haven_auth_unauthorized", handleUnauthorized);
      window.removeEventListener("focus", handleFocus);
      clearInterval(interval);
    };
  }, [logout, verifySession]);

  const login = (token, user) => {
    localStorage.setItem("authToken", token);
    localStorage.setItem("authUser", JSON.stringify(user));
    setToken(token);
    setUser(user);
  };

  const isUserActive =
    Boolean(token && user) &&
    user?.status !== "inactive" &&
    user?.status !== "deactivated";

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: isUserActive,
        login,
        logout,
        verifySession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
